import { v4 } from 'uuid';

import { requestApi } from '@app/api';
import { withDocument } from '@app/api/paths';
import RequesterFieldType from '@app/store/contract-setup/RequesterFieldType';
import FontType from '@app/store/FontType';
import TextAlign from '@app/store/TextAlign';
import { Field } from '@app/store/transport-layer/types/field';
import { FieldGroup } from '@app/store/transport-layer/types/fieldGroup';
import { WithId } from '@app/store/transport-layer/types/utility';

import { getDocumentsParticipants } from '../participants';

interface RequesterFields {
  checkboxInputChecked: boolean | null;
  description: string | null;
  fileUrl: string | null;
  inputType: string;
  positionX: string;
  positionY: string;
  positionZ: number;
  positionPage: number;
  sizeHeight: string | null;
  sizeWidth: string | null;
  textStyleAlign: string | null;
  textStyleFontSize: number | null;
  textStyleFontType: string | null;
  textInputContent: string | null;
  dataLabel: string;
}

function convertDomainFieldType(fieldType: string) {
  switch (fieldType) {
    case 'checkbox':
      return RequesterFieldType.CHECKBOX_FIELD;
    case 'image':
      return RequesterFieldType.IMAGE_FIELD;
    case 'text':
      return RequesterFieldType.TEXT_FIELD;
    default:
      return fieldType;
  }
}

function convertDomainFontType(fontType: string | null) {
  switch (fontType) {
    case 'Noto Sans':
      return FontType.NOTO_SANS;
    case 'Noto Serif':
      return FontType.NOTO_SERIF;
    default:
      return fontType;
  }
}

function convertDomainTextAlign(textAlign: string | null) {
  switch (textAlign) {
    case 'CENTER':
      return TextAlign.CENTER;
    case 'LEFT':
      return TextAlign.LEFT;
    case 'RIGHT':
      return TextAlign.RIGHT;
    default:
      return TextAlign.LEFT;
  }
}

const convertToDeprecatedRequesterField = (field: WithId<Field['ToReceive']>) => {
  const result: WithId<RequesterFields> = {
    positionX: `${field.guide.position.x}`,
    positionY: `${field.guide.position.y}`,
    positionZ: field.guide.position.z,
    positionPage: field.guide.position.page,
    id: field.id,
    sizeHeight: `${field.guide.size.height}`,
    sizeWidth: `${field.guide.size.width}`,
    dataLabel: field.dataLabel,
    checkboxInputChecked: null,
    fileUrl: null,
    inputType: '',
    textStyleAlign: null,
    textStyleFontSize: null,
    textStyleFontType: null,
    textInputContent: null,
    description: null,
  };

  switch (field.type) {
    case 'CHECKBOX':
      result.checkboxInputChecked = field.input?.value === 'TRUE';
      result.inputType = 'checkbox';
      break;
    case 'TEXT':
      result.textInputContent = field.input ? field.input.value : '';
      result.inputType = 'text';
      result.textStyleAlign = field.config.textAlign;
      result.textStyleFontSize = field.config.fontStyle.size;
      result.textStyleFontType =
        field.config.fontStyle.type === 'NOTO_SANS' ? 'Noto Sans' : 'Noto Serif';
      break;
    case 'IMAGE':
      result.inputType = 'image';
      result.fileUrl = field.input && field.input.value ? field.input.value : null;
      break;
    default:
      throw new Error(`The field type "${field.type}" is not supported for the requester field`);
  }
  return result;
};

interface FieldResponse {
  fields: WithId<Field['ToReceive']>[];
  fieldGroups: FieldGroup[];
}

/**
 * GET 참여자 필드들 조회
 * @deprecated Endpoint is removed and use integrated to other path in standardize-document-content
 * @see https://www.notion.so/modusign/bb8e24e723434ecc946dfecb85db5ee3#4d905026de2e48c4a6c868a35917faf0
 */
export function getRequesterFields({
  documentId,
  workspaceId,
}: {
  documentId: string;
  workspaceId: string;
}) {
  // TODO: (standardize-document-content): 추후 코드 정리할때 반환 타입 명시하기
  // FYR: https://github.com/modusign/modusign-client/pull/6367/commits/d15ed21f344cf3f8f754ee513b2c06162f73c36a#r870991773
  return getDocumentsParticipants({ documentId, workspaceId })
    .then((participants) => participants.participants.find((p) => p.type === 'REQUESTER')?.id)
    .then((participantId) => {
      return requestApi<FieldResponse>({
        url: withDocument(
          `/workspaces/${workspaceId}/documents/${documentId}/participants/${participantId}/fields`
        ),
        method: 'GET',
        credentials: true,
      });
    })
    .then((response) => response.data.fields.map(convertToDeprecatedRequesterField))
    .then((inputValues) => {
      return {
        requesterFields: inputValues.map((inputValue) => {
          const inputType = convertDomainFieldType(inputValue.inputType);
          if (inputType === RequesterFieldType.TEXT_FIELD) {
            return {
              ...inputValue,
              id: v4(),
              inputType: convertDomainFieldType(inputValue.inputType),
              positionX: Number(inputValue.positionX),
              positionY: Number(inputValue.positionY),
              positionZ: Number(inputValue.positionZ),
              sizeWidth: Number(inputValue.sizeWidth),
              sizeHeight: Number(inputValue.sizeHeight),
              textStyleAlign: convertDomainTextAlign(inputValue.textStyleAlign),
              textStyleFontSize: inputValue.textStyleFontSize,
              textStyleFontType: convertDomainFontType(inputValue.textStyleFontType),
            };
          }

          return {
            ...inputValue,
            id: v4(),
            inputType: convertDomainFieldType(inputValue.inputType),
            positionX: Number(inputValue.positionX),
            positionY: Number(inputValue.positionY),
            positionZ: Number(inputValue.positionZ),
            sizeWidth: Number(inputValue.sizeWidth),
            sizeHeight: Number(inputValue.sizeHeight),
          };
        }),
      };
    });
}
